import apiService from '@/share/util/api/service';
import config from '../config';
import handler from '../util/apiResultValidator';
import util from '@/share/util/utils';
import saml from '@/share/util/saml';
import Error from '@/common/nodejs/errors';
import i18nResourceLoader from '../../../execute/src/i18n/geti18nLoad';

const idp_name = {
  "ja": "統合故障診断認証システム",
  "en": "Integrated Diagnosis Authentication System",
  "zh": "Integrated Diagnosis Authentication System",
  "it": "Integrated Diagnosis Authentication System",
  "es": "Integrated Diagnosis Authentication System",
  "fr": "Integrated Diagnosis Authentication System",
  "de": "Integrated Diagnosis Authentication System",
  "pl": "Integrated Diagnosis Authentication System",
  "ru": "Integrated Diagnosis Authentication System"
};
const providerLabels = {
  [config.SCI_DOMAIN]: {
    "ja": "Subaru Canada Inc",
    "en": "Subaru Canada Inc",
    "zh": "Subaru Canada Inc",
    "it": "Subaru Canada Inc",
    "es": "Subaru Canada Inc",
    "fr": "Subaru Canada Inc",
    "de": "Subaru Canada Inc",
    "ru": "Subaru Canada Inc"
  },
  [config.SOA_DOMAIN]: {
    "ja": "Subaru of America Inc",
    "en": "Subaru of America Inc",
    "zh": "Subaru of America Inc",
    "it": "Subaru of America Inc",
    "es": "Subaru of America Inc",
    "fr": "Subaru of America Inc",
    "de": "Subaru of America Inc",
    "ru": "Subaru of America Inc"
  },
  [config.THIRD_PARTY_DOMAIN]: {
    "ja": "Third Party of SOA and SCI",
    "en": "Third Party of SOA and SCI",
    "zh": "Third Party of SOA and SCI",
    "it": "Third Party of SOA and SCI",
    "es": "Third Party of SOA and SCI",
    "fr": "Third Party of SOA and SCI",
    "de": "Third Party of SOA and SCI",
    "ru": "Third Party of SOA and SCI"
  }
};

export default {
  namespaced: true,

  state: {
    language: util.getWindowNavigatorLanguage(config.LANGUAGE_LIST),
    versionInfo: {
      systemInfo: null,
      serviceInfo: null,
      displayVersion: config.VERSION_UNKNOWN_DISPLAY,
      copyright: util.isChinaResion() ? "Copyright © 斯巴鲁汽车（中国）有限公司" : "Copyright © SUBARU CORPORATION 2020 All Rights Reserved.",
      isMaintenance: false,
    },
    samlAccountList: [],
    isMaintenance: false,
    envActive: '',
    maintenanceDateTimeRange: {},
    isExternalError: false,
    securityNumber: "11010502044169",
    icpNumber: "05071176号-1",
  },

  mutations: {

    setLanguage(state, payload) {
      state.language = payload.language;
    },

    setSystemVersion(state, payload) {
      state.versionInfo = {
        systemInfo: payload.systemInfo,
        serviceInfo: payload.serviceInfo,
        displayVersion: payload.displayVersion,
        copyright: payload.copyright
      };
      // バージョン情報取得時にメンテナンス中状態を設定する
      state.isMaintenance = payload.isMaintenance;
      state.maintenanceDateTimeRange = { start: payload.startDateTime, end: payload.endDateTime };
    },

    samlAccountList(state, payload) {
      state.samlAccountList = payload;
    },

    setExternalError(state, payload) {
      state.isExternalError = payload;
    },

    setEnvActive(state, payload) {
      state.envActive = payload;
    }
  },

  actions: {

    // 内部に言語情報を設定する(サーバーには更新しない)
    setLanguage(context, payload) {
      let language = payload.language;
      if (!language) {
        language = 'en';
      }
      const param = { language: language };
      context.commit('setLanguage', param);
    },

    async updateLanguage(context, payload) {

      const language = payload.language;
      const userId = payload.userId;

      if (context.state.language === language) {
        return;
      }
      // i18nリソースの取得開始（既にキャッシュに存在する場合はそれを利用する）
      if (payload.loadExtraMessages) {
        const region = util.getPackJsonRegion();
        await i18nResourceLoader.changeLanguage(region, language);
      }

      // 利用者 ID が取得できていない場合でも画面の表示言語は変更する
      const param = { language: language };
      context.commit('setLanguage', param);
      if (userId) {
        await apiService.postCall(apiService.ADMIN, '/users/' + userId + '/language', param);
      }
    },

    async getSystemInfo(context) {
      await Promise.all([
        context.dispatch('setSystemVersion'),
      ]);
    },

    async setSystemVersion(context) {

      const param = {
        systemInfo: null,
        serviceInfo: null,
        displayVersion: config.VERSION_UNKNOWN_DISPLAY,
        copyright: util.isChinaResion() ? "Copyright © 斯巴鲁汽车（中国）有限公司" : "Copyright © SUBARU CORPORATION 2020 All Rights Reserved.",
        isMaintenance: false,
        startDateTime: '',
        endDateTime: ''
      };

      // バージョン情報は取得が失敗してもエラーとはしない 
      try {
        // バージョン取得APIに変更する 
        const response = await apiService.getParamCall(apiService.MAINT, '/systemVersion');
        handler.validate(
          handler.validateTypes.response, response, null, null,
          () => {
            // 成功した場合
            // responseから取得したバージョンに変更する 
            param.systemInfo = response.data.sys_info;
            param.serviceInfo = response.data.ser_info;
            let displayVersion = response.data.sys_info.version;
            if (util.isChinaResion()) {
              displayVersion = displayVersion + " CN";
            }
            param.displayVersion = displayVersion;

            context.commit('setSystemVersion', param);
          },
          (result) => {
            // メンテナンス中のチェックだけ判定する 
            if (result.causeType === handler.causeTypes.maintenanceError) {
              param.isMaintenance = true;
              param.startDateTime = response.data.details.start_datetime;
              param.endDateTime = response.data.details.end_datetime;
            }
            context.commit('setSystemVersion', param);
          }, null, false);
      } catch (error) {
        context.commit('setSystemVersion', param);
      }
    },

    // サーバから外部連携のリスト情報を取得する 
    async setExternalEndpointList(context, payload) {
      // SAML連携情報を取得する (SAML連携情報は取得に失敗してもエラーとしない)
      try {
        const params = {
          service: payload.type
        };
        const response = await apiService.getParamCall(apiService.ADMIN, '/entrance/authentication', params);

        // Promiseを返すようにする
        return new Promise((resolve) => {
          const samlAccountList = [];
          handler.validate(
            handler.validateTypes.response, response, null, null,
            () => {
              // 成功した場合
              context.dispatch('getSamlAccountList', { response, samlAccountList, payload });
              resolve(true); // 成功時にtrueを返す
            },
            () => {
              // メンテナンス以外の時は失敗としてエラーに飛ばす
              if (response.data.code !== Error.SYSTEM_UNDER_MAINTENANCE) {
                resolve(false); // メンテナンス以外のエラー時にfalseを返す
              } else {
                // メンテナンス中の場合はコマンドサインイン用に連携リストの作成を行う
                context.dispatch('getSamlAccountList', { response, samlAccountList, payload });
                resolve(true); // メンテナンス中は成功としてtrueを返す
              }
            },
            null,
            false
          );
        });
      } catch (error) {
        return false; // エラーが発生した場合はfalseを返す
      }
    },

    // ログイン画面の表示有無を設定する 
    setExternalError(context, payload) {
      context.commit('setExternalError', payload);
    },

    // eslint-disable-next-line no-unused-vars
    extendSessionTimer(context) {
      try {
        apiService.getParamCall(apiService.MAINT, '/systemVersion');
      } catch (err) {
        // noop
      }
    },

    getSamlAccountList(context, { response, samlAccountList, payload }) {
      // 中国の場合はドメイン名は１つなので、ドメインチェックは実施しない
      if (util.isChinaResion()) {
        response.data.endPoints.forEach(system => {
          const data = {
            needSingleLogout: true,
            url: system.url,
            sloLogoutUrl: system.sign_out_url,
            name: system.name[payload.language],
            names: system.name,
            provider: 'SUBARU'
          };
          samlAccountList.push(data);
          return;
        });
        context.commit('samlAccountList', samlAccountList);
        return;
      }

      // 中国環境以外はプロバイダーチェックを実施する
      const getProvider = saml.getProviderFromUrl(window.location.href);
      // STG1のみ、SCI・SOA・SUBSCRIBERのボタン表示名を変える
      if (process.env?.VUE_APP_NEEDS_SSO_SELECTION) {
        Object.keys(response.data.endPoints).forEach(key => {
          if (providerLabels[response.data.endPoints[key].provider]) {
            response.data.endPoints[key].name = providerLabels[response.data.endPoints[key].provider];
          }
        });
      }
      let sbrProviderInfo = null;
      response.data.endPoints.forEach(system => {
        // "name" または "url" が空の場合は外部連携のボタンを非表示
        if (system.name && system.url && system.provider) {
          let sloLogoutUrl = null;
          // プロバイダーが一致するものをsamlAccountListに追加
          // CloudFrontからのアクセスの場合、全部samlAccountListに追加
          if (system.provider.toLowerCase() === getProvider.toLowerCase() || util.isCloudFrontUrl()) {
            // 完全ログアウト用の URL を生成
            let manageUrl = "";
            if (window.location.href.includes("manage") && getProvider === config.SBR_DOMAIN) {
              manageUrl = "/manage/";
            }
            sloLogoutUrl = window.location.origin ? system.sign_out_url + "&logout_uri=" + window.location.origin + manageUrl : "";
            const data = {
              needSingleLogout: system.enable_sign_out_flow,
              provider: system.provider,
              url: system.url,
              sloLogoutUrl: sloLogoutUrl,
              name: system.name[payload.language],
              names: system.name,
              isIDP: system.is_idp,
              isAutoRedirectLogin: true // true:自動連携する false:自動連携せずにIDP画面を表示
            };
            // providerがSBRのみ特別処理
            if (system.provider === config.SBR_DOMAIN && sbrProviderInfo === null) {
              data.isAutoRedirectLogin = false;
              // クラウドフロントの時はSUBARU of データ登録者は自動連携
              // IDP独自ユーザーは自動連携なし
              if (util.isCloudFrontUrl()) {
                data.isAutoRedirectLogin = true; // IDP独自ユーザーの使用有無
                sbrProviderInfo = { ...data };
                sbrProviderInfo.name = idp_name[payload.language];
                sbrProviderInfo.names = idp_name;
                sbrProviderInfo.isAutoRedirectLogin = false;
              }
            }
            samlAccountList.push(data);
          }
        }
      });
      // クラウドフロントURLでaccessしてきた場合、IDP独自連携データを追加
      if (sbrProviderInfo !== null) {
        // IDP独自連携情報を先頭に追加
        samlAccountList.unshift(sbrProviderInfo);
      }
      context.commit('samlAccountList', samlAccountList);
      context.commit('setEnvActive', response.data.env_active);
    },
  }
};